import * as React from "react";
import { Link } from "react-router-dom";

import cx from "classnames";

import ThemeContext from "~/common/ThemeContext";
import type { ListeningPorts } from "~/lib/desktopApi/open-in-desktop-app-ports";
import { hasOwnProperty } from "~/lib/js";
import store from "~/lib/store";
import { desktopDownloadLink, inVideocallMode, isMobile, isSafari, isWeb } from "~/lib/utils";
import { Button } from "~/ui";
import Checkbox, { type CheckboxProps } from "~/ui/legacy/Checkbox";
import toast, { ToastActionButton } from "~/ui/toast";

import { ReactComponent as FellowLogo } from "./fellow-logo-squircle.svg";

import styles from "./open-in-desktop-app.module.css";

const { useCallback, useContext, useLayoutEffect, useState } = React;

const OPEN_IN_DESKTOP_STORE_KEY = "alwaysOpenInDesktopApp";
export const openInDesktopStorage = store.namespace(OPEN_IN_DESKTOP_STORE_KEY);
export const currentUserStorageKey = String(window.INITIAL_STATE?.user?.id);

const alwaysOpenInDesktopApp = Boolean(openInDesktopStorage.get(currentUserStorageKey));

/** A checkbox that's always in dark mode, since we're gonna use it inside
 * a toast, which always has a dark background. */
function DarkCheckbox(props: React.PropsWithoutRef<CheckboxProps>) {
	const { isDarkMode } = useContext(ThemeContext);

	return (
		<div className={cx(styles.darkCheckboxWrapper, { "invert-theme": !isDarkMode })}>
			<Checkbox {...props} />
		</div>
	);
}

function Intercept({ onBypass }: { onBypass(): void }) {
	return (
		<div className={styles.intercept}>
			<div className={styles.inner}>
				<FellowLogo className={styles.logo} />
				<h1 className={styles.message}>Launched in Fellow desktop app</h1>
				<Button onClick={onBypass}>Open here instead</Button>
			</div>
			<div className={styles.bottomMessage}>
				You can always change this in your{" "}
				<Link onClick={onBypass} to="/settings/profile/">
					settings
				</Link>
			</div>
		</div>
	);
}

const LISTENING_PORTS: ListeningPorts = [65040, 52254, 60962];

export function getDesktopURL(port: number, pathname: string, params: Record<string, string> = {}) {
	const url = new URL(`/fellow/${pathname}`, "http://127.0.0.1");
	url.port = port.toString();
	url.search = new URLSearchParams(params).toString();
	return url;
}

async function getOpenDesktopPort(): Promise<number | null> {
	if (isSafari) return null;
	for (const port of LISTENING_PORTS) {
		const url = getDesktopURL(port, "can-navigate", { url: window.location.href });
		let response: Response;
		try {
			const rawResponse = await fetch(url.toString(), {
				mode: "cors",
				method: "GET",
			});
			if (!rawResponse.ok) continue;
			response = await rawResponse.json();
		} catch (e) {
			continue;
		}
		if (hasOwnProperty(response, "canNavigate") && typeof response.canNavigate === "boolean") {
			return response.canNavigate ? port : null;
		}
	}
	return null;
}

async function openInDesktopApp(port?: number | null): Promise<void> {
	if (port) {
		const url = getDesktopURL(port, "open", { url: window.location.href });
		await fetch(url.toString(), {
			method: "POST",
		});
	} else {
		window.open(`fellow:${window.location.href}`, "_self");
	}
}

/** In this environment, is prompting/auto-opening in desktop app enabled?
 *
 * We don't do it in all situations. For instance, it defeats the point of an embedded view to kick out to another app. */
export const inApplicableEnvironment = isWeb && !inVideocallMode && !isMobile && window.parent === window;

/** The pathname when this window first loaded – storing it here because by the time the
 * component loads, we might've automatically been redirected to some content by another
 * component. */
const initialWindowPathname = window.location.pathname;

/** We store this in history state so that if you refresh the page or navigate after
 * opening in desktop app, it won't do it again. */
const hasAlreadyIntercepted = Boolean(window.history.state?.hasSentToDesktop);

export default React.memo(function OpenInDesktopInterceptor({ children }: { children: React.ReactElement }) {
	const [intercepting, setIntercepting] = useState(false);
	const doIntercept = useCallback(() => {
		window.history.replaceState({ ...window.history.state, hasSentToDesktop: true }, "");
		setIntercepting(true);
	}, []);
	const bypassIntercept = useCallback(() => setIntercepting(false), []);

	useLayoutEffect(() => {
		async function talkToDesktopApp() {
			if (hasAlreadyIntercepted) return;
			if (!window.INITIAL_STATE?.user?.id) return;
			if (!inApplicableEnvironment) return;
			if (initialWindowPathname === "/") return;
			if (window.INITIAL_STATE.user.type === "GUEST") return;

			const isInBrowserWithoutDetection = isSafari;

			const port = await getOpenDesktopPort();
			if (port === null && !isInBrowserWithoutDetection) return;

			const parsedUrl = new URL(window.location.href);
			const searchParams = parsedUrl.searchParams;
			const desktop = searchParams.get("fromdesktop");

			if (alwaysOpenInDesktopApp && desktop === null) {
				await openInDesktopApp(port);
				doIntercept();
			} else {
				const unsureIfAppIsInstalled = port === null;
				// eslint-disable-next-line fellow/no-inline-disable
				/* eslint-disable react/jsx-no-bind */
				toast.info(
					<>
						<div className={styles.toastMessage}>
							{unsureIfAppIsInstalled ? (
								<>
									Automatically open links in the <a href={desktopDownloadLink}>Fellow desktop app</a>.
								</>
							) : (
								<>It looks like you have the desktop app installed</>
							)}
						</div>
						<label className={styles.toastCheckboxLabel}>
							<DarkCheckbox
								green
								className={styles.toastCheckbox}
								onChange={e => {
									openInDesktopStorage.set(currentUserStorageKey, e.currentTarget.checked);
								}}
							/>
							Always open in the app
						</label>
					</>,
					{
						autoClose: 10000,
						closeOnClick: false,
						closeButton: true,
						title: "Open in desktop app?",
						actions: (
							<ToastActionButton variant="secondary" onClick={() => openInDesktopApp(port)}>
								Open in app
							</ToastActionButton>
						),
					},
				);
				/* eslint-enable react/jsx-no-bind */
			}
		}
		talkToDesktopApp();
	}, [doIntercept]);

	return intercepting ? <Intercept onBypass={bypassIntercept} /> : children;
});
